import React, { FC, useEffect, useState } from 'react';
import { Input, Pagination, Radio, Table } from 'antd';
import type { TableProps } from 'antd/es/table';
import { SorterResult } from 'antd/es/table/interface';
import { useNavigate } from 'react-router-dom';
import { DEFAULT_PAGINATION_PAGE, DEFAULT_VALUE_0, LIST_LIMIT } from 'common/config';
import { EOrderDirection, ESocketEvent } from 'common/const/enum';
import { getDashboardEventListRadioOptions } from 'common/helpers';
import { useAppDispatch, useAppSelector, useNotification, useSocket } from 'common/hooks';
import { IEvent, IEventListParams, IEventListItem } from 'entities/Events/Event.models';
import { StatisticsPanel } from 'entities/Statistics/components';
import { EEventStatus } from 'entities/Events/Event.enum';
import { getEventAvailableOperations, renderEventListRecords } from 'entities/Events/Event.helper';

const { Search } = Input;

export const Dashboard: FC = () => {
  const { eventList, eventListLoading, eventListCount, eventListWatchUpdates, auth, eventLoading } = useAppSelector((state) => ({
    eventList: state.eventList.data,
    eventListLoading: state.eventList.loading,
    eventListCount: state.eventList.count,
    eventListWatchUpdates: state.eventList.watchUpdates,
    auth: state.auth.data,
    eventLoading: state.event.loading,
  }));
  const { eventList: eventListDispatch, event: eventDispatch } = useAppDispatch();
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [search, setSearch] = useState<string>('');
  const [params, setParams] = useState<IEventListParams>({
    status: EEventStatus.Inbox,
    limit: LIST_LIMIT,
  });
  const navigate = useNavigate();
  const { newEventNotificationContext, showNewEventNotification } = useNotification();

  useEffect(() => {
    eventListDispatch.getEventList(params);
  }, [params]);

  useSocket(
    ESocketEvent.Events,
    (event: IEvent) => {
      if (eventListWatchUpdates) {
        eventListDispatch.getEventList(params);

        if (event.status === EEventStatus.Inbox) {
          showNewEventNotification();
        }
      }
    },
    [eventListWatchUpdates],
  );

  const currentUserId = auth?.access?.id;
  const role = auth?.access?.rights[0];

  const onSearchClick = () => {
    setPage(DEFAULT_PAGINATION_PAGE);
    setParams((prev) => ({ ...prev, search, offset: DEFAULT_VALUE_0 }));
  };

  const onStatusChange = (value: string) => {
    const newParams = {
      ...params,
      offset: DEFAULT_VALUE_0,
      status: value,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    // @ts-ignore
    setParams(newParams);
  };

  const onTableChange: TableProps<IEventListItem>['onChange'] = (p, f, s) => {
    const sorter = s as SorterResult<IEventListItem>;
    const newParams = {
      ...params,
      orderField: sorter.field,
      orderDirection: sorter.order ? (sorter.order === 'ascend' ? EOrderDirection.ASC : EOrderDirection.DESC) : undefined,
      offset: DEFAULT_VALUE_0,
    };

    setPage(DEFAULT_PAGINATION_PAGE);
    // @ts-ignore
    setParams(newParams);
  };

  const onEventListItemClick = async (id: number) => {
    const event = await eventDispatch.getEventById(id);

    if (event) {
      const { canReview } = getEventAvailableOperations({
        role,
        currentUserId,
        status: event.status,
        staffId: event.staffId,
        physicianId: event.physicianId,
      });

      if (canReview) {
        eventDispatch.reviewEvent({
          id,
          onSuccess: () => navigate(`/event/${id}`),
        });
      } else {
        navigate(`/event/${id}`);
      }
    }
  };

  const onPageChange = (value: number) => {
    const newParams = {
      ...params,
      offset: LIST_LIMIT * (value - 1),
    };

    setPage(value);
    setParams(newParams);
  };

  return (
    <div className="dashboard">
      <StatisticsPanel />

      <div className="dashboard__content">
        <div className="mb-7">
          <Search
            className="dashboard__content-search"
            placeholder="Search by patient / assignee"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onSearch={onSearchClick}
          />
        </div>

        <div className="mb-9">
          <Radio.Group
            value={params.status}
            onChange={(e) => onStatusChange(e.target.value)}
            buttonStyle="solid"
            options={getDashboardEventListRadioOptions()}
            optionType="button"
          />
        </div>

        <div className="mb-9">
          <Table
            pagination={false}
            dataSource={eventList.map((event) => ({ ...event, key: event.id }))}
            columns={renderEventListRecords(params.status)}
            onChange={onTableChange}
            onRow={(record) => ({ onClick: () => onEventListItemClick(record.id) })}
            loading={eventListLoading || eventLoading}
          />
        </div>

        <Pagination
          current={page}
          total={eventListCount}
          showSizeChanger={false}
          onChange={onPageChange}
          defaultPageSize={LIST_LIMIT}
        />
      </div>

      {newEventNotificationContext}
    </div>
  );
};
